/**
 * SubDetail — Nested view: single sub/trade detail inside the modal.
 * Invoice + document rows share HubDocRowActions (Edit/Delete).
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchProjectInbox,
  uploadContractorForSub,
  checkContractorSubUploadDuplicate,
} from '../../../api/projects'
import type { ProjectTrade, HubInboxItem } from '../../../api/types'
import ComingSoonModal from '../../../components/ComingSoonModal'
import DocUploadModal from '../../../components/docs/DocUploadModal'
import DocTypeEditModal from '../../../components/docs/DocTypeEditModal'
import HubDocRowActions from '../../../components/docs/HubDocRowActions'
import HubDocDeleteConfirm from '../../../components/docs/HubDocDeleteConfirm'
import { useHubUploadRowActions } from '../../../hooks/useHubUploadRowActions'
import { statusLabel } from './inboxStatus'
import TrustScoreBadge from '../../../components/trust/TrustScoreBadge'
import JobcostErrorBoundary from './JobcostErrorBoundary'
import SubCommitmentCard from './SubCommitmentCard'

function fmtAmt(raw: string | null | undefined): string {
  const n = parseFloat(raw ?? '0')
  if (!n) return '—'
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
}
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
}
function initials(name: string | null): string {
  if (!name) return '?'
  return name.split(/\s+/).slice(0,2).map(w => w[0].toUpperCase()).join('')
}

function statusChip(status: string): { label: string; cls: string } {
  const label = statusLabel(status)
  if (status === 'approved') return { label, cls: 'c-grn' }
  if (status === 'rejected') return { label, cls: 'c-red' }
  if (status === 'changes_requested') return { label, cls: 'c-amb' }
  return { label, cls: 'c-amb' }
}

interface Props {
  projectId:       number
  trade:           ProjectTrade
  jobcostOn?:      boolean
  onBack:          () => void
  onViewInboxItem: (item: HubInboxItem) => void
}

export default function SubDetail({ projectId, trade, jobcostOn = false, onBack, onViewInboxItem }: Props) {
  const [showRequestDocModal, setShowRequestDocModal] = useState(false)
  const [showAddDocModal, setShowAddDocModal] = useState(false)
  const qc = useQueryClient()
  const actions = useHubUploadRowActions(projectId, trade.company_name ?? trade.name)

  const { data: tradeInbox = [], isPending } = useQuery({
    queryKey:  ['project-inbox', projectId, trade.project_trade_id, trade.id],
    queryFn:   () => fetchProjectInbox(projectId, {
      hubTradeId:       trade.project_trade_id ?? undefined,
      projectVendorId: trade.project_trade_id ? undefined : trade.id,
    }),
    staleTime: 30_000,
    enabled:   Boolean(trade.project_trade_id || trade.id),
  })

  const invoices = tradeInbox.filter(i => i.doc_type === 'invoice' || i.doc_type === 'change_order')
  const docs     = tradeInbox.filter(i => i.doc_type !== 'invoice' && i.doc_type !== 'change_order')

  const totalApproved = tradeInbox
    .filter(i => i.status === 'approved')
    .reduce((s, i) => s + parseFloat(i.amount ?? '0'), 0)
  const totalPending = tradeInbox
    .filter(i => i.status === 'pending')
    .reduce((s, i) => s + parseFloat(i.amount ?? '0'), 0)

  return (
    <div data-testid="sub-detail-pane">
      <div className="pm-back-bar" onClick={onBack}>
        <span className="arr">←</span> Back to Subs &amp; Inbox
      </div>

      <div className="pm-sd-hdr">
        <div className="pm-sd-av" style={{ background: 'var(--amber)' }}>
          {initials(trade.company_name ?? trade.name)}
        </div>
        <div>
          <div className="pm-sd-nm">{trade.company_name ?? trade.name}</div>
          <div className="pm-sd-tr">
            <span>{trade.name}</span>
            {trade.contact_name && <span>· {trade.contact_name}</span>}
            {trade.contact_email && <span>· {trade.contact_email}</span>}
          </div>
          {trade.vendor_user_id != null && (
            <TrustScoreBadge vendorUserId={trade.vendor_user_id} size="md" />
          )}
          {jobcostOn && (
            <JobcostErrorBoundary>
              <SubCommitmentCard
                projectId={projectId}
                vendorId={trade.vendor_id}
                companyName={trade.company_name}
                enabled={jobcostOn}
              />
            </JobcostErrorBoundary>
          )}
        </div>
        <div />
      </div>

      <div className="pm-sd-stats" data-testid="sub-detail-stats">
        <div className="pm-sd-stat">
          <div className="pm-sd-stat-l">Approved on this project</div>
          <div className="pm-sd-stat-v" data-testid="sub-detail-approved">{fmtAmt(String(totalApproved))}</div>
        </div>
        <div className="pm-sd-stat">
          <div className="pm-sd-stat-l">Pending review</div>
          <div className="pm-sd-stat-v" data-testid="sub-detail-pending">{fmtAmt(String(totalPending))}</div>
        </div>
        <div className="pm-sd-stat">
          <div className="pm-sd-stat-l">Documents on file</div>
          <div className="pm-sd-stat-v" data-testid="sub-detail-docs-count">{docs.length}</div>
        </div>
      </div>

      <div style={{ padding: '18px 22px' }}>
        <div className="pm-section-h">
          <div>
            <div className="pm-section-tt">Invoices on this project</div>
            <div className="pm-section-st">Click any to review</div>
          </div>
        </div>
        <div className="panel" style={{ marginBottom: 18 }}>
          {isPending ? (
            <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--ink4)' }}>Loading…</div>
          ) : invoices.length === 0 ? (
            <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--ink4)' }}>
              No invoices submitted yet.
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Submitted</th>
                  <th>Notes</th>
                  <th className="r">Amount</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invoices.map(item => {
                  const { label, cls } = statusChip(item.status)
                  return (
                    <tr
                      key={item.id}
                      data-testid={`sub-invoice-row-${item.id}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onViewInboxItem(item)}
                    >
                      <td className="mono" style={{ fontSize: 11 }}>{item.original_name}</td>
                      <td className="mono" style={{ fontSize: 11 }}>{fmtDate(item.created_at)}</td>
                      <td>{item.notes ?? '—'}</td>
                      <td className="r mono-c">{fmtAmt(item.amount)}</td>
                      <td><span className={`chip ${cls}`}>{label}</span></td>
                      <td className="hub-row-actions-cell">
                        <HubDocRowActions
                          item={item}
                          testIdPrefix="sub-invoice"
                          onEdit={actions.setEditTarget}
                          onDelete={(i) => void actions.openDelete(i)}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="pm-section-h">
          <div>
            <div className="pm-section-tt">Documents on file</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn b-ol btn-sm"
              onClick={() => setShowAddDocModal(true)}
              data-testid="sub-detail-add-doc-btn"
            >
              + Add document
            </button>
            <button
              className="btn b-ol btn-sm"
              onClick={() => setShowRequestDocModal(true)}
              data-testid="sub-detail-request-doc-btn"
            >
              + Request doc
            </button>
          </div>
        </div>
        <div className="panel" style={{ marginBottom: 18 }}>
          {docs.length === 0 ? (
            <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--ink4)' }}>
              No compliance documents on file.
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>File</th>
                  <th>Filed</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {docs.map(item => {
                  const { label, cls } = statusChip(item.status)
                  return (
                    <tr key={item.id} data-testid={`sub-doc-row-${item.id}`}>
                      <td>{item.doc_type.replace(/_/g, ' ')}</td>
                      <td className="mono" style={{ fontSize: 11 }}>{item.original_name}</td>
                      <td className="mono" style={{ fontSize: 11 }}>{fmtDate(item.created_at)}</td>
                      <td><span className={`chip ${cls}`}>{label}</span></td>
                      <td className="hub-row-actions-cell">
                        <HubDocRowActions
                          item={item}
                          testIdPrefix="sub-doc"
                          onEdit={actions.setEditTarget}
                          onDelete={(i) => void actions.openDelete(i)}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="pm-section-h">
          <div><div className="pm-section-tt">Notes</div></div>
        </div>
        <div className="panel" style={{ marginBottom: 18 }}>
          <div style={{ padding: '14px 16px' }}>
            <textarea
              placeholder={`Add a note about ${trade.company_name ?? trade.name} on this project…`}
              style={{
                padding: '8px 11px', border: '1px solid var(--line2)',
                borderRadius: 'var(--rs)', fontSize: 12, fontFamily: 'inherit',
                color: 'var(--ink)', background: 'var(--surface)', resize: 'vertical',
                minHeight: 48, width: '100%',
              }}
            />
          </div>
        </div>
      </div>

      <ComingSoonModal
        isOpen={showRequestDocModal}
        title="Request Document — Coming Soon"
        body="Coming soon — document request workflow is in development as part of PR-V4-DOC-REVIEW. For now, message the sub directly via the inbox."
        onClose={() => setShowRequestDocModal(false)}
      />
      {showAddDocModal && (
        <DocUploadModal
          title="Add document for sub"
          defaultType="insurance"
          showInsuranceExpiry
          existingFiles={tradeInbox}
          checkDuplicate={(f) => checkContractorSubUploadDuplicate(projectId, trade.id, f)}
          onClose={() => setShowAddDocModal(false)}
          onSuccess={() => {
            setShowAddDocModal(false)
            void qc.invalidateQueries({ queryKey: ['project-inbox', projectId] })
            void qc.invalidateQueries({ queryKey: ['project-vault', projectId] })
          }}
          upload={async (file, docType, opts, onProgress) => {
            await uploadContractorForSub(projectId, trade.id, file, docType, opts, onProgress)
          }}
        />
      )}

      {actions.editTarget && (
        <DocTypeEditModal
          currentType={actions.editTarget.doc_type}
          currentExpires={actions.editTarget.insurance_expires}
          currentAmount={actions.editTarget.amount}
          fileName={actions.editTarget.original_name}
          onClose={() => actions.setEditTarget(null)}
          onSave={actions.saveEdit}
        />
      )}

      {actions.deleteTarget && actions.deleteCopy && (
        <HubDocDeleteConfirm
          fileName={actions.deleteCopy.fileName}
          body={actions.deleteCopy.body}
          subSentLine={actions.deleteCopy.subSentLine}
          busy={actions.deleteBusy}
          error={actions.deleteErr}
          onCancel={() => actions.closeDelete()}
          onConfirm={() => void actions.confirmDelete()}
        />
      )}
    </div>
  )
}
